import React from 'react';

import { gettext } from '@eventbrite/i18n';
import type { CalendarProps, FooterSignalProps } from '@eventbrite/timed-entry';
import {
    CalendarType,
    DateAvailabilityStatus,
    EventDate,
} from '../../../state/infiniteCalendarSeries';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../../utils/rebrand';
import { DiscountSvg } from '../assests/DiscountSvg';
import { SoldOutIconSvg } from '../assests/SoldOutIconSvg';
import { COLORS } from '../colorConstants';
import { getOverallDiscount } from '../TimeSlotContainer/utils';
import defaultStyles from './CalendarContainer.module.scss';
import rebrandStyles from './CalendarContainer.rebranding.module.scss';
import { hasValidDiscount } from './utils';

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};

export interface GetFooterSignals {
    isAppRebrandActive: boolean;
    calendarType: CalendarType;
    calendarDates: EventDate[];
    currency?: string;
}

export const getCalendarDateCellProps = (
    isAppRebrandActive: boolean,
): CalendarProps<DateAvailabilityStatus>['dateCellProps'] => {
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];
    return {
        available: {
            icon: null,
            enabled: true,
            className: styles.availableDateCell,
        },
        discount: {
            icon: (
                <DiscountSvg
                    fill={
                        isAppRebrandActive
                            ? COLORS['blue-300']
                            : COLORS['orange-500']
                    }
                />
            ),
            enabled: true,
            className: styles.discountDateCell,
        },
        soldOut: {
            icon: <SoldOutIconSvg />,
            enabled: true,
            className: styles.soldOutDateCell,
        },
        highDemand: {
            enabled: true,
            icon: null,
            className: styles.highDemandDateCell,
        },
        unavailable: {
            enabled: false,
            icon: null,
            className: styles.unavailableDateCell,
        },
    };
};

export const getFooterSignals = ({
    isAppRebrandActive,
    calendarType,
    calendarDates,
    currency,
}: GetFooterSignals): Array<FooterSignalProps> => {
    const signals: Array<FooterSignalProps> = [];

    const discountSignal = getDiscountSignalProps(
        calendarDates,
        isAppRebrandActive,
        currency,
    );
    if (discountSignal) {
        signals.push(discountSignal);
    }

    signals.push(getHighDemandSignalProps(isAppRebrandActive));

    signals.push(getSoldOutSignalProps(calendarType, isAppRebrandActive));

    return signals;
};

const getDiscountSignalProps = (
    calendarDates: EventDate[],
    isAppRebrandActive: boolean,
    currency?: string,
): FooterSignalProps | null => {
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];
    const hasDiscount = calendarDates?.some((date) =>
        hasValidDiscount(date.discount),
    );

    if (!hasDiscount) {
        return null;
    }

    return {
        icon: (
            <DiscountSvg
                fill={
                    isAppRebrandActive
                        ? COLORS['blue-300']
                        : COLORS['orange-500']
                }
            />
        ),
        className: styles.discountFooter,
        text: getOverallDiscount(calendarDates, currency),
    };
};

const getHighDemandSignalProps = (
    isAppRebrandActive: boolean,
): FooterSignalProps => {
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];
    return {
        className: styles.highDemandFooter,
        text: gettext('High demand').toString(),
    };
};

const getSoldOutSignalProps = (
    calendarType: CalendarType,
    isAppRebrandActive: boolean,
): FooterSignalProps => {
    const shouldShowSoldOutIcon = calendarType === CalendarType.scrollCalendar;
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];
    return {
        icon: shouldShowSoldOutIcon ? <SoldOutIconSvg /> : undefined,
        className: styles.soldOutFooter,
        text: gettext('Sold out').toString(),
    };
};
